import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { message } from "antd";

import { IUseLegalInvestorDocuments } from "./IUseLegalInvestorDocuments";
import { ContractData, ContractLegalInvestorDocuments, InvestorDTO, Document, ContractDocumentsErrors, Representative, Beneficiaries, FundsOriginDocumentErrors } from "../../Models";

import { emptyContract, emptyContractLegalInvestorDocuments, emptyInvestor } from "../../Assets/Data/EmptyValues";
import { FileTypes } from "../../Assets/Enums";
import { getFundSourceDocumentOptions } from "../../Assets/OptionsTranslations";

export const useLegalInvestorDocuments = ({
	translations,
	userIdInstitution,
	contractAPI,
	investorsAPI,
	documentsAPI,
	beneficiariesAPI,
	representativesAPI,
	unlockNextStep,
}: IUseLegalInvestorDocuments) => {
	const [searchParams] = useSearchParams();
	const location = useLocation();

	const [loading, setLoading] = useState<boolean>(false);
	const [noContent, setNoContent] = useState<boolean>(false);
	const [contractData, setContractData] = useState<ContractData>(emptyContract);
	const [errors, setErrors] = useState<ContractDocumentsErrors>({});
	const [investors, setInvestors] = useState<InvestorDTO[]>([]);
	const [representatives, setRepresentatives] = useState<Representative[]>([]);
	const [beneficiaries, setBeneficiaries] = useState<Beneficiaries[]>([]);
	const [contractDocuments, setContractDocuments] = useState<ContractLegalInvestorDocuments>(emptyContractLegalInvestorDocuments);
	const [showSecondDocument, setShowSecondDocument] = useState<boolean>(false);
	const [showMandatoryErrors, setShowMandatoryErrors] = useState<boolean>(false);
	const mandatoryTaxDomicileInstitutionIds = [2855, 2295];
	const isTaxDomicileCertificateMandatory = mandatoryTaxDomicileInstitutionIds.includes(Number(contractData.idInstitution || userIdInstitution));
	const normalizedEntityNationality = (investors[0]?.entityNationality || investors[0]?.nationality || '').trim().toLowerCase();
	const isTaxIdentificationCardMandatory = normalizedEntityNationality !== 'portugal';
	const normalizedTaxResidenceCountry = (investors[0]?.countryTaxResidence || '').trim().toLowerCase();
	const isPortugueseTaxResidence = normalizedTaxResidenceCountry === 'portugal';

	const normalizeIdentityValue = (value?: string) => (value || '').trim().toUpperCase();
	const parseParticipationPercentage = (value: unknown): number => {
		if (typeof value === 'number') {
			return Number.isFinite(value) ? value : 0;
		}
		if (typeof value === 'string') {
			const normalizedValue = value.trim().replace(',', '.');
			const parsedValue = Number(normalizedValue);
			return Number.isFinite(parsedValue) ? parsedValue : 0;
		}
		return 0;
	};
	const getBeneficiaryParticipationPercentage = (beneficiary?: Beneficiaries) => parseParticipationPercentage(beneficiary?.participationPercentage);
	const shouldRequireBeneficiaryIdDocument = (beneficiary?: Beneficiaries) => {
		const participationPercentage = getBeneficiaryParticipationPercentage(beneficiary);
		return participationPercentage === 0
			|| participationPercentage >= 25;
	};

	const getRepresentativeIdentity = (representative?: Representative) => normalizeIdentityValue(representative?.documentNumber);
	const getBeneficiaryIdentity = (beneficiary?: Beneficiaries) => normalizeIdentityValue(beneficiary?.nif);

	const syncIdentityDocuments = (
		docs: ContractLegalInvestorDocuments,
		sourceType: 'Representative' | 'Beneficiary',
		documentKind: 'idDocument' | 'taxDomicileCertificate',
		identityValue: string,
		fileUrl: string,
		normalizedType: string,
	) => {
		if (!identityValue) {
			return docs;
		}

		const sourceDocumentsKey: PersonDocumentCollectionKey = documentKind === 'idDocument'
			? (sourceType === 'Representative' ? 'representativesIdDocuments' : 'beneficiariesIdDocuments')
			: (sourceType === 'Representative' ? 'representativesTaxDomicileCertificates' : 'beneficiariesTaxDomicileCertificates');

		const targetCollections: Array<{
			documentsKey: PersonDocumentCollectionKey;
			personIds: number[];
		}> = [];

		const matchingRepresentativeIds = representatives
			.filter((representative) => getRepresentativeIdentity(representative) === identityValue)
			.map((representative) => representative.id)
			.filter((id) => id > 0);
		const matchingBeneficiaryIds = beneficiaries
			.filter((beneficiary) => getBeneficiaryIdentity(beneficiary) === identityValue)
			.map((beneficiary) => beneficiary.id)
			.filter((id) => id > 0);

		if (documentKind === 'idDocument') {
			targetCollections.push(
				{ documentsKey: 'representativesIdDocuments', personIds: matchingRepresentativeIds },
				{ documentsKey: 'beneficiariesIdDocuments', personIds: matchingBeneficiaryIds },
			);
		} else {
			targetCollections.push(
				{ documentsKey: 'representativesTaxDomicileCertificates', personIds: matchingRepresentativeIds },
				{ documentsKey: 'beneficiariesTaxDomicileCertificates', personIds: matchingBeneficiaryIds },
			);
		}

		targetCollections.forEach(({ documentsKey, personIds }) => {
			if (personIds.length === 0) {
				return;
			}

			const existingDocuments = [...(docs[documentsKey] || [])];
			personIds.forEach((personId) => {
				const existingDocIndex = existingDocuments.findIndex((doc: Document) => Number(doc.idRegistry) === Number(personId));
				if (existingDocIndex !== -1) {
					existingDocuments[existingDocIndex] = {
						...existingDocuments[existingDocIndex],
						url: fileUrl,
						status: 'Draft',
						type: normalizedType,
						idRegistry: personId,
					};
				} else {
					existingDocuments.push({ url: fileUrl, type: normalizedType, status: 'Draft', idRegistry: personId });
				}
			});
			docs[documentsKey] = existingDocuments;
		});

		docs[sourceDocumentsKey] = [...(docs[sourceDocumentsKey] || [])];
		return docs;
	};
	
	useEffect(() => {
		setInvestors([emptyInvestor]);
		setContractData(emptyContract);
		setShowSecondDocument(false);
		setShowMandatoryErrors(false);
	}, [location.pathname]);

	useEffect(() => {
		if (searchParams.has('ContractId')) {
			configureInitialData(Number(searchParams.get('ContractId')));
		} else if (searchParams.has('hash')) {
			const hash = searchParams.get('hash');
			const getContractIdByHash = async(hash: string) => {
				return await contractAPI.getContractByHash(hash).then((res: any) => {
					if (res.success) {
						configureInitialData(Number(res.data));
					} else {
						message.error(translations.messageError);
						setNoContent(true);
					}
				})
				.catch((err: any) => {
					message.error(translations.messageError);
				})
			}
		
			getContractIdByHash(hash!);
		} else {
			setInvestors([emptyInvestor]);
		}
	}, []);

	const buildInitialErrors = (docs: ContractLegalInvestorDocuments): ContractDocumentsErrors => {
		const initialErrors: ContractDocumentsErrors = {};
		const fundsOriginErrors: { [key: string]: FundsOriginDocumentErrors } = {};
		(docs.fundsOriginDocuments || []).forEach((doc) => {
			if (doc.type) {
				fundsOriginErrors[doc.type] = {};
			}
		});
		initialErrors.contract = fundsOriginErrors;
		return initialErrors;
	};

	const configureInitialData = async(ContractId: number) => {
		setLoading(true);
		setErrors({});
		setShowMandatoryErrors(false);
		await contractAPI.getContract(ContractId).then(async(contractResponse: any) => {
			if (contractResponse.success) {
				const contract = contractResponse.data;
				investorsAPI.getInvestors(ContractId).then(async(resInvestors: any) => {
					const initialErrors = buildInitialErrors(emptyContractLegalInvestorDocuments);
					setContractData(contract);
					setInvestors(resInvestors.data);
					setContractDocuments(emptyContractLegalInvestorDocuments);
					setErrors(initialErrors);
					setShowSecondDocument(false);
					await documentsAPI.getLegalInvestorDocuments(ContractId).then((resDocuments: any) => {
						if (resDocuments.success) {
							setContractDocuments(resDocuments.data);
							setShowSecondDocument(Boolean(resDocuments.data.representativesDocumentFile?.url));
							setLoading(false);
						}
					});
				});
				representativesAPI.getLegalRepresentatives(ContractId).then((response: any) => {
					if (response.success) {
						setRepresentatives(response.data);
					}
				});
				beneficiariesAPI.getBeneficiaries(ContractId).then((response: any) => {
					if (response.success) {
						setBeneficiaries((response.data || []).filter((beneficiary: Beneficiaries) => shouldRequireBeneficiaryIdDocument(beneficiary)));
					}
				});
			} else {
				setContractData({ ...contractData, idInstitution: userIdInstitution });
				setNoContent(true);
			}
		}).catch((err: any) => console.log(err));
		setLoading(false);
	};

	type PersonDocumentCollectionKey =
		| 'representativesIdDocuments'
		| 'beneficiariesIdDocuments'
		| 'representativesTaxDomicileCertificates'
		| 'beneficiariesTaxDomicileCertificates';

	const handleUpdatePersonDocument = (
		index: number,
		source: string,
		personType: string,
		documentsKey: PersonDocumentCollectionKey,
	) => (fileUrl: string) => {
		const docs: ContractLegalInvestorDocuments = { ...contractDocuments };
		const person = personType === 'Representative' ? representatives[index] : beneficiaries[index];
		const personId = person?.id || 0;
		const normalizedType = FileTypes[source] || source;
		const personDocuments = [...(docs[documentsKey] || [])];

		// Find existing document for this specific person
		const existingDocIndex = personDocuments.findIndex(
			(doc: Document) => Number(doc.idRegistry) === Number(personId) && personId > 0
		);
		
		// Fallback only applies if:
		// 1. No existing document found for this person
		// 2. The document at this index has no valid idRegistry (unassigned draft)
		// 3. The document at this index has no URL yet (truly unassigned)
		const fallbackIndex = existingDocIndex === -1
			&& index < personDocuments.length
			&& Number(personDocuments[index]?.idRegistry || 0) <= 0
			&& !personDocuments[index]?.url
			? index
			: existingDocIndex;
		
		if (fallbackIndex !== -1) {
			const existingDoc = personDocuments[fallbackIndex];
			personDocuments[fallbackIndex] = {
				...existingDoc,
				url: fileUrl,
				status: 'Draft',
				type: normalizedType,
				idRegistry: personId,
			};
		} else {
			// Create new document for this person
			personDocuments.push({ url: fileUrl, type: normalizedType, status: 'Draft', idRegistry: personId });
		}

		docs[documentsKey] = personDocuments;
		if (documentsKey === 'representativesIdDocuments' || documentsKey === 'beneficiariesIdDocuments') {
			const identityValue = personType === 'Representative'
				? getRepresentativeIdentity(person as Representative)
				: getBeneficiaryIdentity(person as Beneficiaries);
			syncIdentityDocuments(docs, personType as 'Representative' | 'Beneficiary', 'idDocument', identityValue, fileUrl, normalizedType);
		}
		setContractDocuments(docs);
		setErrors(buildInitialErrors(docs));
	};

	const handleUpdateIdDocument = (index: number, source: string, personType: string) => {
		const documentsKey: PersonDocumentCollectionKey = personType === 'Representative'
			? 'representativesIdDocuments'
			: 'beneficiariesIdDocuments';
		return handleUpdatePersonDocument(index, source, personType, documentsKey);
	};

	const handleUpdateTaxDomicileDocument = (index: number, personType: string) => {
		const documentsKey: PersonDocumentCollectionKey = personType === 'Representative'
			? 'representativesTaxDomicileCertificates'
			: 'beneficiariesTaxDomicileCertificates';
		return handleUpdatePersonDocument(index, 'taxDomicileCertificate', personType, documentsKey);
	};

	const getFundSourceType = (source: string) => {
		const normalizedSource = source.trim();
		return FileTypes[normalizedSource] || normalizedSource;
	};

	const getExistingFundSourceDocumentIndex = (documents: Document[], source: string) => {
		const normalizedSource = source.trim();
		const normalizedType = getFundSourceType(source);
		const matchingDocuments = documents
			.map((doc, index) => ({ doc, index }))
			.filter(({ doc }) => doc.type === normalizedType || doc.type === normalizedSource);

		return matchingDocuments.find(({ doc }) => Boolean(doc.url))?.index ?? matchingDocuments[0]?.index ?? -1;
	};

	const deduplicateFundSourceDocuments = (documents: Document[], source: string, keepIndex: number) => {
		const normalizedSource = source.trim();
		const normalizedType = getFundSourceType(source);
		return documents.filter((doc, index) => {
			if (index === keepIndex) {
				return true;
			}
			return doc.type !== normalizedType && doc.type !== normalizedSource;
		});
	};

	const handleUpdateFundSourceDocument = (source: string) => (fileUrl: string) => {
		const normalizedType = getFundSourceType(source);
		const docs: ContractLegalInvestorDocuments = {
			...contractDocuments,
			fundsOriginDocuments: [...(contractDocuments.fundsOriginDocuments || [])],
		};
		const existingDocIndex = getExistingFundSourceDocumentIndex(docs.fundsOriginDocuments, source);
		if (existingDocIndex !== -1) {
			const existingDoc = docs.fundsOriginDocuments[existingDocIndex];
			docs.fundsOriginDocuments[existingDocIndex] = {
				...existingDoc,
				url: fileUrl,
				status: 'Draft',
				type: normalizedType,
				idRegistry: 0,
			};
			docs.fundsOriginDocuments = deduplicateFundSourceDocuments(docs.fundsOriginDocuments, source, existingDocIndex);
		} else {
			docs.fundsOriginDocuments.push({ url: fileUrl, type: normalizedType, status: 'Draft', idRegistry: 0 });
		}
		setContractDocuments(docs);
		setErrors(buildInitialErrors(docs));
	};

	const handleUpdateDocument = (fileType: keyof ContractLegalInvestorDocuments) => (fileUrl: string) => {
		let updatedDocuments: ContractLegalInvestorDocuments = { ...contractDocuments };
		const currentCertificate = updatedDocuments[fileType] as Document || { url: '', type: FileTypes[fileType as keyof typeof FileTypes], status: '' };
		updatedDocuments = {
			...updatedDocuments,
			[fileType]: {
				...currentCertificate,
				type: FileTypes[fileType as keyof typeof FileTypes],
				url: fileUrl,
				status: 'Draft',
				idRegistry: 0
			},
		};
		setContractDocuments(updatedDocuments);
		setErrors(buildInitialErrors(updatedDocuments));
	};

	const handleDocumentTypeChange = (source: string) => (value: string) => {
		const normalizedType = getFundSourceType(source);
		const updatedDocuments: ContractLegalInvestorDocuments = {
			...contractDocuments,
			fundsOriginDocuments: [...(contractDocuments.fundsOriginDocuments || [])],
		};
		const existingDocIndex = getExistingFundSourceDocumentIndex(updatedDocuments.fundsOriginDocuments, source);
		if (existingDocIndex === -1) {
			return;
		}

		const existingDoc = updatedDocuments.fundsOriginDocuments[existingDocIndex];
		updatedDocuments.fundsOriginDocuments[existingDocIndex] = {
			...existingDoc,
			type: normalizedType,
			description: value,
			status: 'Draft',
		};
		updatedDocuments.fundsOriginDocuments = deduplicateFundSourceDocuments(updatedDocuments.fundsOriginDocuments, source, existingDocIndex);
		setContractDocuments(updatedDocuments);
		setErrors(buildInitialErrors(updatedDocuments));
	};

	const splitDocumentsByPersistence = (documents: ContractLegalInvestorDocuments) => {
		let documentsToSave: ContractLegalInvestorDocuments | null = emptyContractLegalInvestorDocuments;
		let documentsToUpdate: ContractLegalInvestorDocuments | null = emptyContractLegalInvestorDocuments;

		const newDocsGroup: ContractLegalInvestorDocuments = {
			...emptyContractLegalInvestorDocuments,
			contractId: documents.contractId,
			fundsOriginDocuments: [],
			representativesIdDocuments: [],
			representativesTaxDomicileCertificates: [],
			beneficiariesIdDocuments: [],
			beneficiariesTaxDomicileCertificates: [],
		};
		const existingDocsGroup: ContractLegalInvestorDocuments = {
			...emptyContractLegalInvestorDocuments,
			contractId: documents.contractId,
			fundsOriginDocuments: [],
			representativesIdDocuments: [],
			representativesTaxDomicileCertificates: [],
			beneficiariesIdDocuments: [],
			beneficiariesTaxDomicileCertificates: [],
		};

		if (documents.articlesOfIncorporation) {
			if (documents.articlesOfIncorporation.id) {
				existingDocsGroup.articlesOfIncorporation = documents.articlesOfIncorporation;
			} else if (documents.articlesOfIncorporation.url) {
				newDocsGroup.articlesOfIncorporation = documents.articlesOfIncorporation;
			}
		}
		if (documents.statutesOrRegulations) {
			if (documents.statutesOrRegulations.id) {
				existingDocsGroup.statutesOrRegulations = documents.statutesOrRegulations;
			} else if (documents.statutesOrRegulations.url) {
				newDocsGroup.statutesOrRegulations = documents.statutesOrRegulations;
			}
		}
		if (documents.statutesModification) {
			if (documents.statutesModification.id) {
				existingDocsGroup.statutesModification = documents.statutesModification;
			} else if (documents.statutesModification.url) {
				newDocsGroup.statutesModification = documents.statutesModification;
			}
		}
		if (documents.representativesDocumentFile) {
			if (documents.representativesDocumentFile.id) {
				existingDocsGroup.representativesDocumentFile = documents.representativesDocumentFile;
			} else if (documents.representativesDocumentFile.url) {
				newDocsGroup.representativesDocumentFile = documents.representativesDocumentFile;
			}
		}
		if(documents.regulatedEntityRegistry) {
			if (documents.regulatedEntityRegistry.id) {
				existingDocsGroup.regulatedEntityRegistry = documents.regulatedEntityRegistry;
			} else if (documents.regulatedEntityRegistry.url) {
				newDocsGroup.regulatedEntityRegistry = documents.regulatedEntityRegistry;
			}
		}
		if (documents.taxIdentificationCard) {
			if (documents.taxIdentificationCard.id) {
				existingDocsGroup.taxIdentificationCard = documents.taxIdentificationCard;
			} else if (documents.taxIdentificationCard.url) {
				newDocsGroup.taxIdentificationCard = documents.taxIdentificationCard;
			}
		}
		if (documents.beneficialOwnershipCertificate) {
			if (documents.beneficialOwnershipCertificate.id) {
				existingDocsGroup.beneficialOwnershipCertificate = documents.beneficialOwnershipCertificate;
			} else if (documents.beneficialOwnershipCertificate.url) {
				newDocsGroup.beneficialOwnershipCertificate = documents.beneficialOwnershipCertificate;
			}
		}
		if (documents.representativePowerOfAttorney) {
			if (documents.representativePowerOfAttorney.id) {
				existingDocsGroup.representativePowerOfAttorney = documents.representativePowerOfAttorney;
			} else if (documents.representativePowerOfAttorney.url) {
				newDocsGroup.representativePowerOfAttorney = documents.representativePowerOfAttorney;
			}
		}
		if (documents.bankCertificate) {
			if (documents.bankCertificate.id) {
				existingDocsGroup.bankCertificate = documents.bankCertificate;
			} else if (documents.bankCertificate.url) {
				newDocsGroup.bankCertificate = documents.bankCertificate;
			}
		}

		(documents.fundsOriginDocuments || []).forEach((document) => {
			if (document.id) {
				existingDocsGroup.fundsOriginDocuments.push(document);
			} else if (document.url) {
				newDocsGroup.fundsOriginDocuments.push(document);
			}
		});
		(documents.representativesIdDocuments || []).forEach((document) => {
			if (document.id) {
				existingDocsGroup.representativesIdDocuments.push(document);
			} else if (document.url) {
				newDocsGroup.representativesIdDocuments.push(document);
			}
		});
		(documents.representativesTaxDomicileCertificates || []).forEach((document) => {
			if (document.id) {
				existingDocsGroup.representativesTaxDomicileCertificates.push(document);
			} else if (document.url) {
				newDocsGroup.representativesTaxDomicileCertificates.push(document);
			}
		});
		(documents.beneficiariesIdDocuments || []).forEach((document) => {
			if (document.id) {
				existingDocsGroup.beneficiariesIdDocuments.push(document);
			} else if (document.url) {
				newDocsGroup.beneficiariesIdDocuments.push(document);
			}
		});
		(documents.beneficiariesTaxDomicileCertificates || []).forEach((document) => {
			if (document.id) {
				existingDocsGroup.beneficiariesTaxDomicileCertificates.push(document);
			} else if (document.url) {
				newDocsGroup.beneficiariesTaxDomicileCertificates.push(document);
			}
		});

		if (documents.w8BEN) {
			if (documents.w8BEN.id) {
				existingDocsGroup.w8BEN = documents.w8BEN;
			} else if (documents.w8BEN.url) {
				newDocsGroup.w8BEN = documents.w8BEN;
			}
		}
		if (documents.w9) {
			if (documents.w9.id) {
				existingDocsGroup.w9 = documents.w9;
			} else if (documents.w9.url) {
				newDocsGroup.w9 = documents.w9;
			}
		}
		if (documents.tin) {
			if (documents.tin.id) {
				existingDocsGroup.tin = documents.tin;
			} else if (documents.tin.url) {
				newDocsGroup.tin = documents.tin;
			}
		}
		if (documents.taxResidenceCertificate) {
			if (documents.taxResidenceCertificate.id) {
				existingDocsGroup.taxResidenceCertificate = documents.taxResidenceCertificate;
			} else if (documents.taxResidenceCertificate.url) {
				newDocsGroup.taxResidenceCertificate = documents.taxResidenceCertificate;
			}
		}

		const hasNewDocuments = Boolean(
			newDocsGroup.articlesOfIncorporation ||
			newDocsGroup.statutesOrRegulations ||
			newDocsGroup.statutesModification ||
			newDocsGroup.representativesDocumentFile ||
			newDocsGroup.regulatedEntityRegistry ||
			newDocsGroup.taxIdentificationCard ||
			newDocsGroup.beneficialOwnershipCertificate ||
			newDocsGroup.representativePowerOfAttorney ||
			newDocsGroup.bankCertificate ||
			newDocsGroup.fundsOriginDocuments.length > 0 ||
			newDocsGroup.representativesIdDocuments.length > 0 ||
			newDocsGroup.representativesTaxDomicileCertificates.length > 0 ||
			newDocsGroup.beneficiariesIdDocuments.length > 0 ||
			newDocsGroup.beneficiariesTaxDomicileCertificates.length > 0 ||
			newDocsGroup.w8BEN ||
			newDocsGroup.w9 ||
			newDocsGroup.tin ||
			newDocsGroup.taxResidenceCertificate
		);
		const hasExistingDocuments = Boolean(
			existingDocsGroup.articlesOfIncorporation ||
			existingDocsGroup.statutesOrRegulations ||
			existingDocsGroup.statutesModification ||
			existingDocsGroup.representativesDocumentFile ||
			existingDocsGroup.regulatedEntityRegistry ||
			existingDocsGroup.taxIdentificationCard ||
			existingDocsGroup.beneficialOwnershipCertificate ||
			existingDocsGroup.representativePowerOfAttorney ||
			existingDocsGroup.bankCertificate ||
			existingDocsGroup.fundsOriginDocuments.length > 0 ||
			existingDocsGroup.representativesIdDocuments.length > 0 ||
			existingDocsGroup.representativesTaxDomicileCertificates.length > 0 ||
			existingDocsGroup.beneficiariesIdDocuments.length > 0 ||
			existingDocsGroup.beneficiariesTaxDomicileCertificates.length > 0 ||
			existingDocsGroup.w8BEN ||
			existingDocsGroup.w9 ||
			existingDocsGroup.tin ||
			existingDocsGroup.taxResidenceCertificate
		);

		if (hasNewDocuments) {
			documentsToSave = { ...newDocsGroup };
		} else {
			documentsToSave = null;
		}
		if (hasExistingDocuments) {
			documentsToUpdate = { ...existingDocsGroup };
		} else {
			documentsToUpdate = null;
		}

		return { documentsToSave, documentsToUpdate };
	};

	const areAllDocumentsUploaded = () => {
		const hasDocumentForPerson = (documents: Document[], personId?: number) => {
			if (!personId || personId <= 0) {
				return false;
			}
			return documents.some((doc: Document) => Number(doc.idRegistry) === Number(personId) && Boolean(doc.url && doc.type));
		};

		const hasArticlesOfIncorporation = Boolean(contractDocuments.articlesOfIncorporation?.url && contractDocuments.articlesOfIncorporation.type);
		const hasRepresentativesDocumentFile = showSecondDocument 
			? Boolean(contractDocuments.representativesDocumentFile?.url && contractDocuments.representativesDocumentFile.type)
			: true;
		const hasTaxIdentificationCard = !isTaxIdentificationCardMandatory
			|| Boolean(contractDocuments.taxIdentificationCard?.url && contractDocuments.taxIdentificationCard.type);
		const hasBeneficialOwnershipCertificate = Boolean(contractDocuments.beneficialOwnershipCertificate?.url && contractDocuments.beneficialOwnershipCertificate.type);
		const hasBankCertificate = Boolean(contractDocuments.bankCertificate?.url && contractDocuments.bankCertificate.type);
		const fundsOriginDocs = contractDocuments.fundsOriginDocuments || [];
		const hasFundsDocuments =
			fundsOriginDocs.length > 0 &&
			fundsOriginDocs.every((fundsOriginDoc: Document) => Boolean(fundsOriginDoc.url && fundsOriginDoc.type));
		const representativesIdDocuments = contractDocuments.representativesIdDocuments || [];
		const hasRepresentativesIdDocuments = representatives.length > 0
			&& representatives.every((rep) => hasDocumentForPerson(representativesIdDocuments, rep.id));
		const beneficiariesIdDocuments = contractDocuments.beneficiariesIdDocuments || [];
		const hasBeneficiariesIdDocuments = beneficiaries.length === 0
			|| beneficiaries.every((beneficiary) => hasDocumentForPerson(beneficiariesIdDocuments, beneficiary.id));
		let hasW8BEN = true;
		let hasW9 = true;
		let hasTIN = true;
		let hasTaxResidenceCertificate = true;
		if (investors.length > 0 && investors[0].countryTaxResidence !== 'España') {
			hasW8BEN = !Boolean(investors[0]?.american) ? Boolean(contractDocuments.w8BEN?.url) : true;
			hasW9 = Boolean(investors[0]?.american) ? Boolean(contractDocuments.w9?.url) : true;
			hasTaxResidenceCertificate = isPortugueseTaxResidence
				? true
				: Boolean(contractDocuments.taxResidenceCertificate?.url);
		}

		return hasArticlesOfIncorporation && hasRepresentativesDocumentFile && hasTaxIdentificationCard
			&& hasFundsDocuments && hasBeneficialOwnershipCertificate && hasBankCertificate && hasRepresentativesIdDocuments
			&& hasBeneficiariesIdDocuments
			&& hasW8BEN && hasW9 && hasTIN && hasTaxResidenceCertificate;
	};

	const getFundsOriginOptions = (source: string) => {
		const normalizedSource = source.trim();
		const normalizedSourceType = FileTypes[normalizedSource] || normalizedSource;
		const documentOptions = getFundSourceDocumentOptions(translations, normalizedSourceType, true);
		
		return documentOptions.map((option: string) => {
			return {
				label: option,
				value: option,
			}
		});
	};

	const hasUrlAndType = (document?: Document | null) => Boolean(document?.url && document?.type);

	const getPersonDocument = (documents: Document[] = [], personId?: number, fallbackIndex?: number) => {
		if (personId && personId > 0) {
			const personDocument = documents.find((doc: Document) => Number(doc.idRegistry) === Number(personId));
			if (personDocument) {
				return personDocument;
			}
		}

		if (fallbackIndex !== undefined && fallbackIndex < documents.length) {
			const fallbackDocument = documents[fallbackIndex];
			if (Number(fallbackDocument?.idRegistry || 0) <= 0) {
				return fallbackDocument;
			}
		}

		return null;
	};

	const isMandatoryMainDocumentMissing = (
		fileType: 'articlesOfIncorporation' | 'representativesDocumentFile' | 'taxIdentificationCard' | 'beneficialOwnershipCertificate' | 'bankCertificate'
	) => {
		if (!showMandatoryErrors) {
			return false;
		}

		if (fileType === 'representativesDocumentFile' && !showSecondDocument) {
			return false;
		}

		if (fileType === 'taxIdentificationCard' && !isTaxIdentificationCardMandatory) {
			return false;
		}

		return !hasUrlAndType(contractDocuments[fileType] as Document | null);
	};

	const isMandatoryTaxResidenceDocumentMissing = (
		fileType: 'w8BEN' | 'w9' | 'taxResidenceCertificate'
	) => {
		if (!showMandatoryErrors) {
			return false;
		}

		if (investors.length === 0 || investors[0].countryTaxResidence === 'España') {
			return false;
		}

		if (fileType === 'w8BEN') {
			return !Boolean(investors[0]?.american) && !Boolean(contractDocuments.w8BEN?.url);
		}

		if (fileType === 'w9') {
			return Boolean(investors[0]?.american) && !Boolean(contractDocuments.w9?.url);
		}

		return !isPortugueseTaxResidence && !Boolean(contractDocuments.taxResidenceCertificate?.url);
	};

	const isMandatoryFundSourceDocumentMissing = (source: string) => {
		if (!showMandatoryErrors) {
			return false;
		}

		const normalizedSource = source.trim();
		const normalizedType = getFundSourceType(source);
		const document = (contractDocuments.fundsOriginDocuments || []).find(
			(doc: Document) => doc.type === normalizedType || doc.type === normalizedSource
		);

		return !Boolean(document?.url);
	};

	const isMandatoryPersonDocumentMissing = (
		personType: 'Representative' | 'Beneficiary',
		index: number,
		documentKind: 'idDocument' | 'taxDomicileCertificate'
	) => {
		if (!showMandatoryErrors) {
			return false;
		}

		if (documentKind === 'taxDomicileCertificate') {
			return false;
		}

		const person = personType === 'Representative' ? representatives[index] : beneficiaries[index];
		if (!person) {
			return false;
		}

		const personDocuments = documentKind === 'idDocument'
			? (personType === 'Representative'
				? contractDocuments.representativesIdDocuments
				: contractDocuments.beneficiariesIdDocuments)
			: (personType === 'Representative'
				? contractDocuments.representativesTaxDomicileCertificates
				: contractDocuments.beneficiariesTaxDomicileCertificates);

		const document = getPersonDocument(personDocuments || [], person.id, index);
		return !hasUrlAndType(document);
	};

	const isPersonDocumentMandatory = (
		personType: 'Representative' | 'Beneficiary',
		index: number,
		documentKind: 'idDocument' | 'taxDomicileCertificate'
	) => {
		if (documentKind === 'taxDomicileCertificate') {
			return false;
		}

		if (personType === 'Representative') {
			return true;
		}

		return shouldRequireBeneficiaryIdDocument(beneficiaries[index]);
	};

	const validateDocumentsData = () => {
		let isValid = true;
		const contractDocumentsErrors = buildInitialErrors(contractDocuments);
		(contractDocuments.fundsOriginDocuments || []).forEach((fundsOriginDoc: Document) => {
			if (!contractDocumentsErrors.contract) {
				contractDocumentsErrors.contract = {};
			}
			const sourceKey = fundsOriginDoc.type || 'unknown';
			if (!contractDocumentsErrors.contract[sourceKey]) {
				contractDocumentsErrors.contract[sourceKey] = {};
			}
			if (!fundsOriginDoc.url && fundsOriginDoc.description) {
				contractDocumentsErrors.contract[sourceKey].fundsOriginFileNotUploaded = true;
				isValid = false;
			}
			if (fundsOriginDoc.url && !fundsOriginDoc.description) {
				contractDocumentsErrors.contract[sourceKey].fundsOriginTypeNotSelected = true;
				isValid = false;
			}
		});
		setErrors(contractDocumentsErrors);
		return isValid;
	};

	const saveData = async () => {
		setLoading(true);
		setShowMandatoryErrors(true);
		const isValid = validateDocumentsData();
		if (!isValid) {
			setLoading(false);
			return;
		}

		const contractId = Number(searchParams.get('ContractId'));

		const { documentsToSave, documentsToUpdate } = splitDocumentsByPersistence(contractDocuments);

		try {
			const responses: any[] = [];
			if (documentsToSave) {
				responses.push(await documentsAPI.saveLegalInvestorDocuments(documentsToSave));
			}
			if (documentsToUpdate) {
				responses.push(await documentsAPI.updateLegalInvestorDocuments(documentsToUpdate));
			}
			const hasErrors = responses.some((res) => !res?.success);
			if (hasErrors) {
				message.error(translations.messageError);
			} else {
				message.success(translations.datosGuardados);
				if (responses.length > 0) {
					await configureInitialData(contractId);
				}
				if (areAllDocumentsUploaded()) {
					unlockNextStep(contractId);
				}
			}
		} catch (error) {
			console.error(error);
			message.error(translations.messageError);
		} finally {
			setLoading(false);
		}
	};

	return {
		loading,
		noContent,
		contractData,
		investors,
		representatives,
		beneficiaries,
		contractDocuments,
		errors,
		handleUpdateIdDocument,
		handleUpdateTaxDomicileDocument,
		handleUpdateFundSourceDocument,
		handleUpdateDocument,
		handleDocumentTypeChange,
		saveData,
		areAllDocumentsUploaded,
		getFundsOriginOptions,
		showSecondDocument,
		setShowSecondDocument,
		isTaxDomicileCertificateMandatory,
		isTaxIdentificationCardMandatory,
		isMandatoryMainDocumentMissing,
		isMandatoryTaxResidenceDocumentMissing,
		isMandatoryFundSourceDocumentMissing,
		isMandatoryPersonDocumentMissing,
		isPersonDocumentMandatory,
	};
}
